import { DEFAULT_LOCALE } from "./constants";
import { I18N_TRANSLATION_CATALOGS } from "./generated/translationCatalogs";
import type { SupportedLocale, TranslationCatalogs } from "./types";

interface LookupTranslationOptions {
  catalogs: TranslationCatalogs;
  fallbackLocale: SupportedLocale;
  locale: SupportedLocale;
  namespace: string;
  sourceText: string;
}

type SourceCatalog = Readonly<Record<string, string>>;

const committedSourceCatalogs: Readonly<Record<string, SourceCatalog>> =
  I18N_TRANSLATION_CATALOGS[DEFAULT_LOCALE];

const sourceHashCache = new WeakMap<SourceCatalog, Map<string, string>>();

export function lookupTranslation(options: LookupTranslationOptions): string {
  const sourceHash = getSourceHash({
    catalogs: options.catalogs,
    namespace: options.namespace,
    sourceText: options.sourceText,
  });

  if (sourceHash === undefined) {
    return options.sourceText;
  }

  return (
    getTranslation({
      catalogs: options.catalogs,
      hash: sourceHash,
      locale: options.locale,
      namespace: options.namespace,
    }) ??
    getTranslation({
      catalogs: options.catalogs,
      hash: sourceHash,
      locale: options.fallbackLocale,
      namespace: options.namespace,
    }) ??
    getTranslation({
      catalogs: options.catalogs,
      hash: sourceHash,
      locale: DEFAULT_LOCALE,
      namespace: options.namespace,
    }) ??
    options.sourceText
  );
}

interface GetSourceHashOptions {
  catalogs: TranslationCatalogs;
  namespace: string;
  sourceText: string;
}

function getSourceHash(options: GetSourceHashOptions): string | undefined {
  const sourceCatalog =
    options.catalogs[DEFAULT_LOCALE]?.[options.namespace] ??
    committedSourceCatalogs[options.namespace];

  if (sourceCatalog === undefined) {
    return undefined;
  }

  const sourceHashIndex = getSourceHashIndex(sourceCatalog);

  return sourceHashIndex.get(options.sourceText);
}

function getSourceHashIndex(sourceCatalog: SourceCatalog): Map<string, string> {
  const cachedIndex = sourceHashCache.get(sourceCatalog);

  if (cachedIndex !== undefined) {
    return cachedIndex;
  }

  const index = new Map<string, string>();

  for (const [hash, sourceText] of Object.entries(sourceCatalog)) {
    index.set(sourceText, hash);
  }

  sourceHashCache.set(sourceCatalog, index);

  return index;
}

interface GetTranslationOptions {
  catalogs: TranslationCatalogs;
  hash: string;
  locale: SupportedLocale;
  namespace: string;
}

function getTranslation(options: GetTranslationOptions): string | undefined {
  return options.catalogs[options.locale]?.[options.namespace]?.[options.hash];
}
